import { Badge } from '@chopchop-se/ui';
import type { DeviceStatus, PrintJobStatus, PrinterStatus } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The status pills for the fleet, the registry and the print queue.
 *
 * Each is a thin map from a domain value to a `Badge` tone plus a translated
 * label — no colour or wording is decided at the call site, so a device that is
 * "stale" looks the same on the fleet list and on its own detail page.
 */

const DEVICE_TONES: Record<DeviceStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  online: 'success',
  idle: 'warning',
  stale: 'danger',
  offline: 'danger',
  inactive: 'neutral',
};

export function DeviceStatusBadge({ status }: { status: DeviceStatus }): ReactNode {
  const { t } = useTranslation('devicesPrinting');
  return (
    <Badge tone={DEVICE_TONES[status]} dot minWidth data-testid={`device-status-${status}`}>
      {t(`status.${status}`)}
    </Badge>
  );
}

const JOB_TONES: Record<PrintJobStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  queued: 'neutral',
  sending: 'info',
  sent: 'success',
  failed: 'warning',
  dead: 'danger',
};

export function PrintJobStatusBadge({ status }: { status: PrintJobStatus }): ReactNode {
  const { t } = useTranslation('devicesPrinting');
  return (
    <Badge tone={JOB_TONES[status]} dot minWidth data-testid={`job-status-${status}`}>
      {t(`jobs.status.${status}`)}
    </Badge>
  );
}

/**
 * A printer's health, derived from the last SGD/HQES poll. "Out of labels"
 * outranks everything else — it is the failure an operator can actually fix,
 * and the one the old system never surfaced at all.
 */
export function PrinterStateBadge({ status }: { status: PrinterStatus | null }): ReactNode {
  const { t } = useTranslation('devicesPrinting');

  if (!status) {
    return <Badge tone="neutral">{t('printers.state.neverPolled')}</Badge>;
  }
  if (!status.online) {
    return (
      <Badge tone="danger" dot>
        {t('printers.state.unreachable')}
      </Badge>
    );
  }
  if (status.mediaOut) {
    return (
      <Badge tone="danger" dot>
        {t('printers.state.mediaOut')}
      </Badge>
    );
  }
  if (status.ribbonOut) {
    return (
      <Badge tone="warning" dot>
        {t('printers.state.ribbonOut')}
      </Badge>
    );
  }
  if (status.headOpen) {
    return (
      <Badge tone="warning" dot>
        {t('printers.state.headOpen')}
      </Badge>
    );
  }
  if (status.paused) {
    return (
      <Badge tone="warning" dot>
        {t('printers.state.paused')}
      </Badge>
    );
  }
  if (status.ready) {
    return (
      <Badge tone="success" dot>
        {t('printers.state.ready')}
      </Badge>
    );
  }
  return <Badge tone="neutral">{t('printers.state.unknown')}</Badge>;
}

/** Derived alarm chips (`battery_low`, `outbox_deep`, …) from the API. */
export function DeviceAlarms({ alarms }: { alarms: readonly string[] }): ReactNode {
  const { t } = useTranslation('devicesPrinting');
  if (alarms.length === 0) return <span className="text-ink-muted">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {alarms.map((alarm) => (
        <Badge
          key={alarm}
          tone={alarm === 'battery_critical' || alarm === 'not_seen' ? 'danger' : 'warning'}
          size="sm"
        >
          {t(`alarms.${alarm}`, { defaultValue: alarm })}
        </Badge>
      ))}
    </div>
  );
}

/** Battery percentage with the same thresholds the alarms use. */
export function BatteryCell({ pct }: { pct: number | null }): ReactNode {
  if (pct === null) return <span className="text-ink-muted">—</span>;
  const tone = pct <= 10 ? 'text-red-600' : pct <= 20 ? 'text-amber-600' : 'text-ink';
  return <span className={tone}>{pct} %</span>;
}
